import React from 'react';
import LegacyContractRenderer from './ContractRenderer';
import ApryseContractRenderer from './ApryseContractRenderer';

export { default as ContractModal } from './ContractModal';
export { default as ApryseContractRenderer } from './ApryseContractRenderer';
export { default as LegacyContractRenderer } from './ContractRenderer';

/**
 * Local error boundary so a contract crash doesn't bring down the whole
 * meeting (the parent meeting error boundary was triggering on contract
 * issues, blocking the meeting itself).
 */
class ContractErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[ContractRenderer] crashed:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="fixed inset-x-0 top-16 bottom-20 z-[40] flex items-center justify-center bg-[#0D1117]/95">
          <div className="bg-[#161B22] border border-red-500/40 rounded-xl p-6 max-w-md text-center">
            <div className="text-red-400 text-lg font-semibold mb-2">Contract failed to load</div>
            <div className="text-gray-400 text-sm mb-4">
              {this.state.error.message || 'An unexpected error occurred'}
            </div>
            <button
              type="button"
              onClick={() => {
                this.setState({ error: null });
                if (typeof this.props.onClose === 'function') this.props.onClose();
              }}
              className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Default to the Apryse renderer for ALL PDF contracts (with or without
// editable fields). Only non-PDF contracts (images, DOCX fallback) continue
// to use the legacy pdf.js-based renderer. A contract can still explicitly
// opt out via `useApryse: false`.
// Normalize: participants receive the contract wrapped in an element envelope
// { type: 'contract', config: { elementData: { ...actualContract } } }.
// Hosts receive it flat. This helper unwraps if needed.
function unwrapContract(raw) {
  if (!raw) return raw || {};
  if (raw.config?.elementData) {
    return { ...raw, ...raw.config.elementData };
  }
  if (raw.settings && typeof raw.settings === 'string') {
    try {
      return { ...raw, ...JSON.parse(raw.settings) };
    } catch (_) {}
  }
  return raw;
}

export function ContractRenderer(props) {
  const rawContract = props.contract || {};
  const contract = unwrapContract(rawContract);
  const fileType = (contract.file_type || '').toLowerCase();
  const url = (contract.file_url || '').toLowerCase();
  // Strip query string + hash before extension check — S3 presigned URLs
  // often look like `.../file.pdf?X-Amz-Signature=...`.
  const cleanPath = url.split('?')[0].split('#')[0];
  const looksLikePdf =
    fileType === 'pdf' ||
    fileType === 'application/pdf' ||
    cleanPath.endsWith('.pdf');

  const useApryse =
    contract.useApryse === false
      ? false
      : contract.useApryse === true ||
        contract.annotationMode === 'apryse' ||
        looksLikePdf;

  if (typeof window !== 'undefined' && window.__RLINK_CONTRACT_DEBUG) {
    console.log('[ContractRenderer dispatch]', {
      useApryse,
      file_type: contract.file_type,
      file_url: contract.file_url,
      editable_fields: contract.editable_fields?.length || 0,
    });
  }

  const Component = useApryse ? ApryseContractRenderer : LegacyContractRenderer;
  // Pass the unwrapped contract so both renderers see fields at top level.
  return (
    <ContractErrorBoundary onClose={props.onClose}>
      <Component {...props} contract={contract} />
    </ContractErrorBoundary>
  );
}
